import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import {
  formatDate,
  formatViews,
  getArticleById,
  getRelatedArticles,
  getSection,
  getSectionName,
} from '@/utils/content';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const related = useMemo(() => (article ? getRelatedArticles(article, 3) : []), [article]);
  const section = article ? getSection(article.section) : undefined;

  const jsonLd = useMemo(() => {
    if (!article) {
      return undefined;
    }
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: article.title,
        description: article.summary,
        datePublished: article.date,
        dateModified: article.date,
        inLanguage: 'zh-CN',
        author: { '@type': 'Organization', name: '17吃瓜网' },
        publisher: { '@type': 'Organization', name: '17吃瓜网' },
        articleSection: getSectionName(article.section),
        keywords: article.tags.join(','),
        url: `${origin}/article/${article.id}`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${origin}/article/${article.id}` },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}/` },
          {
            '@type': 'ListItem',
            position: 2,
            name: getSectionName(article.section),
            item: `${origin}/section/${article.section}`,
          },
          { '@type': 'ListItem', position: 3, name: article.title, item: `${origin}/article/${article.id}` },
        ],
      },
    ];
  }, [article, origin]);

  useSeo({
    title: article ? `${article.title} - 17吃瓜网` : '内容不存在 - 17吃瓜网',
    description: article ? article.summary : '该内容不存在或已下线，返回首页浏览最新吃瓜内容。',
    keywords: article ? `17吃瓜网,${getSectionName(article.section)},${article.tags.join(',')}` : '17吃瓜网',
    path: article ? `/article/${article.id}` : '/',
    type: 'article',
    jsonLd,
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950 md:text-3xl">这篇内容找不到了</h1>
          <p className="mt-3 text-sm text-foreground-600">链接可能已失效，回首页看看最新整理的话题。</p>
          <Link
            to="/"
            className="mt-6 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" /> 返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <article className="w-full bg-background-50">
        <div className="mx-auto w-full px-4 pt-8 md:px-6">
          <Breadcrumbs
            items={[
              { label: '首页', to: '/' },
              { label: getSectionName(article.section), to: `/section/${article.section}` },
              { label: article.title },
            ]}
          />
        </div>

        <header className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500 md:text-sm">
            <Link
              to={`/section/${article.section}`}
              className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              {getSectionName(article.section)}
            </Link>
            <span>{formatDate(article.date)}</span>
            <span>· {article.readTime} 分钟阅读</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" /> {formatViews(article.views)}
            </span>
          </div>

          <h1 className="mt-5 max-w-4xl font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {article.summary}
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-5">
            <span className="flex items-center gap-2 text-sm text-foreground-600">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-user-3-line" />
              </span>
              {article.author}
            </span>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="rounded-full border border-background-200 bg-background-100 px-3 py-1 text-xs text-foreground-600">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </header>

        <div className="mx-auto w-full px-4 md:px-6">
          <div className="relative h-64 w-full overflow-hidden rounded-lg md:h-[420px]">
            <img src={article.cover} alt={article.coverAlt} className="h-full w-full object-top" />
          </div>
        </div>

        <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-lightbulb-flash-line text-accent-700" /> 要点速览
              </h2>
              <ul className="mt-4 space-y-2.5">
                {article.keyPoints.map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 space-y-8">
              {article.blocks.map((block, index) => (
                <section key={block.heading ?? `block-${index}`} id={`block-${index}`} className="scroll-mt-24">
                  {block.heading && (
                    <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{block.heading}</h2>
                  )}
                  <div className="mt-4 space-y-4">
                    {block.paragraphs.map((paragraph, pIndex) => (
                      <p key={`${index}-${pIndex}`} className="text-sm leading-loose text-foreground-700 md:text-base">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center md:p-6">
              <div>
                <p className="font-heading text-base font-bold text-foreground-950">想看这个分区的更多内容？</p>
                <p className="mt-1 text-sm text-foreground-600">
                  回到{getSectionName(article.section)}
                  {section ? `，还有 ${section.tagline} 等着你。` : '继续逛。'}
                </p>
              </div>
              <Link
                to={`/section/${article.section}`}
                className="flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                进入{getSectionName(article.section)} <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">本文目录</h2>
                <ul className="mt-4 space-y-2.5">
                  {article.blocks.map((block, index) => (
                    <li key={block.heading ?? `toc-${index}`}>
                      <a
                        href={`#block-${index}`}
                        className="flex cursor-pointer items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                      >
                        <i className="ri-hashtag mt-0.5 text-foreground-400" />
                        <span>{block.heading ?? `第 ${index + 1} 段`}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 rounded-lg border border-background-200 bg-secondary-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-bookmark-line text-secondary-700" /> 相关推荐
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {related.map((item) => (
                    <ArticleCard key={item.id} article={item} variant="list" />
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </SiteLayout>
  );
}